import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { dsEnums } from '@vectronic/openApi-admin';
import { utcMoment } from '@vectronic/util';
import { cloneDeep } from 'es-toolkit';
import { Subject, debounceTime, filter } from 'rxjs';
import { CameraScheduleService } from '../../../services/camera-schedule.service';
import {
  ScheduleRangeSliderComponent,
  ScheduleRangeSliderValue,
} from '../../schedule-range-slider/schedule-range-slider.component';
import { CameraPreviewComponent } from '../camera-preview/camera-preview.component';

@Component({
  selector: 'rc-camera-slider',
  templateUrl: './camera-slider.component.html',
  styleUrls: ['./camera-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CameraPreviewComponent, ScheduleRangeSliderComponent],
})
export class CameraSliderComponent {
  cameraScheduleService = inject(CameraScheduleService);

  isRuleExpanded = input<boolean>();
  onTime = input<number>();
  offset = input<number>();
  cycleTime = input<number>();
  ruleIndex = input<number>();
  sequenceIndex = input<number>();
  startDate = input<Date | any>();
  pictureInterval = input<number>();
  photoCount = input<number>();
  videoLength = input<number>();
  mode = input<dsEnums.CameraScheduleRuleType>();
  trigger = input<dsEnums.CameraScheduleRuleMainTrigger>();
  readonly = input<boolean>();
  timeInterval = input<number>();

  remove = output<number>();

  isPreviwable = computed(() => {
    return this.cycleTime() && this.onTime() && this.trigger() && this.startDate();
  });

  weekdays: string[];

  private sliderChanges$ = new Subject<ScheduleRangeSliderValue>();

  constructor() {
    this.sliderChanges$
      .pipe(takeUntilDestroyed(), debounceTime(500))
      .subscribe((value) => this.updateCameraSlider(value));

    toObservable(this.startDate)
      .pipe(
        takeUntilDestroyed(),
        filter((v) => v != null),
      )
      .subscribe((startDate) => {
        const startDay = utcMoment(startDate).day();
        const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        this.weekdays = weekdays.map((v, i) => {
          const index = (startDay + i + 6) % 7;
          return weekdays[index];
        });
      });
  }

  onSliderValueChange(value: ScheduleRangeSliderValue) {
    this.sliderChanges$.next(value);
  }

  updateCameraSlider({ offset, duration }: ScheduleRangeSliderValue) {
    if (this.readonly() || !this.isPreviwable() || !this.startDate()) return;
    const rule = cloneDeep(this.cameraScheduleService.rules$.value[this.ruleIndex()]);
    if (!rule) return;
    const newOnTime = duration * 60;
    const startDate = utcMoment(rule.startTime, true);
    const preOffset = startDate.hours() * 60 + startDate.minutes();
    if (rule.onTime !== newOnTime || preOffset !== offset) {
      rule.startTime =
        utcMoment(rule.startTime)
          .set({
            hours: Math.floor((offset % 1440) / 60),
            minutes: offset % 60,
            seconds: 0,
            milliseconds: 0,
          })
          .toISOString()
          .split('.')[0] + 'Z';
      if (!rule.endTime) {
        rule.endTime =
          utcMoment()
            .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
            .add({ year: 25 })
            .toISOString()
            .split('.')[0] + 'Z';
      }
      rule.onTime = newOnTime;
      this.cameraScheduleService.updateRule(this.ruleIndex(), rule, this.readonly());
    }
  }
}
